import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    Video, ExternalLink, Power, BarChart2, Edit, Trash2, Calendar, Loader2, ShieldCheck, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { VirtualClass } from "../../pages/VirtualClasses";

interface VirtualClassTableProps {
    classes: VirtualClass[];
    isLoading: boolean;
    handleToggleStatus: (cls: VirtualClass) => void;
    handleValidate: (cls: VirtualClass) => void;
    handleResyncEdusign: (cls: VirtualClass) => void;
    resyncingClassId: string | null;
    fetchStats: (id: string) => void;
    viewDetails: (cls: VirtualClass) => void;
    openEditModal: (cls: VirtualClass) => void;
    handleDeleteClass: (id: string) => void;
    sortBy: 'start_time' | 'created_at';
    sortOrder: 'asc' | 'desc';
    onSort: (column: 'start_time' | 'created_at') => void;
    canViewStats?: boolean;
}

const VirtualClassTable: React.FC<VirtualClassTableProps> = ({
    classes,
    isLoading,
    handleToggleStatus,
    handleValidate,
    handleResyncEdusign,
    resyncingClassId,
    fetchStats,
    viewDetails,
    openEditModal,
    handleDeleteClass,
    sortBy,
    sortOrder,
    onSort,
    canViewStats = true
}) => {
    const SortIcon = ({ column }: { column: 'start_time' | 'created_at' }) => {
        if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
            : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
    };
    return (
        <Card className="border-slate-200 shadow-sm rounded-2xl bg-white overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Video className="h-4 w-4 text-slate-500" /> Classes Actives
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                            <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest pl-6">Nom</TableHead>
                            <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest cursor-pointer select-none hover:text-slate-700 transition-colors" onClick={() => onSort('start_time')}>
                                <span className="flex items-center">Plage Horaire <SortIcon column="start_time" /></span>
                            </TableHead>
                            <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest">Lien</TableHead>
                            <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest">Créateur</TableHead>
                            <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest cursor-pointer select-none hover:text-slate-700 transition-colors" onClick={() => onSort('created_at')}>
                                <span className="flex items-center">Date Création <SortIcon column="created_at" /></span>
                            </TableHead>
                            <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest text-center">Status</TableHead>
                            <TableHead className="text-right font-bold text-slate-500 uppercase text-[10px] tracking-widest pr-6">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} className="h-32 text-center text-slate-400 font-medium">
                                    <div className="flex justify-center items-center gap-2">
                                        <Loader2 className="h-5 w-5 animate-spin" /> Chargement...
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : classes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="h-32 text-center text-slate-400 font-medium">
                                    Aucune classe virtuelle créée.
                                </TableCell>
                            </TableRow>
                        ) : (
                            classes.map((cls) => {
                                const start = cls.start_time || cls.startTime;
                                const end = cls.end_time || cls.endTime;
                                const created = cls.created_at || cls.createdAt || new Date().toISOString();
                                const creator = cls.created_by || cls.createdBy || 'N/A';

                                return (
                                    <TableRow key={cls.id} className="group hover:bg-slate-50/50 transition-colors">
                                        <TableCell className="font-semibold text-slate-900 pl-6">{cls.name}</TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                                                {start && (
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-slate-400 w-8">Début:</span>
                                                        {new Date(start).toLocaleString()}
                                                    </div>
                                                )}
                                                {end && (
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-slate-400 w-8">Fin:</span>
                                                        {new Date(end).toLocaleString()}
                                                    </div>
                                                )}
                                                {!start && !end && <span className="text-slate-400 italic">Non planifié</span>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <a
                                                href={cls.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                                            >
                                                Rejoindre <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Avatar className="h-6 w-6">
                                                    <AvatarFallback className="bg-slate-100 text-slate-500 text-[10px] font-bold">
                                                        {creator.charAt(0)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="text-sm text-slate-600">{creator}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                                                <Calendar className="h-3 w-3" />
                                                {new Date(created).toLocaleDateString()}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <Badge
                                                    variant={cls.status === 'active' ? 'default' : 'secondary'}
                                                    className={cls.status === 'active' ? "bg-emerald-500 hover:bg-emerald-600" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}
                                                >
                                                    {cls.status === 'active' ? 'Actif' : 'Inactif'}
                                                </Badge>
                                                <Badge
                                                    variant="secondary"
                                                    className={cls.edit_status === 'validated'
                                                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                                        : "bg-amber-100 text-amber-700 hover:bg-amber-200"}
                                                >
                                                    {cls.edit_status === 'validated' ? 'Validé' : 'Brouillon'}
                                                </Badge>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right pr-6">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className={`h-8 w-8 transition-all ${cls.status === 'active' ? 'text-emerald-500 hover:bg-emerald-50' : 'text-slate-400 hover:text-emerald-500 hover:bg-emerald-50'}`}
                                                    onClick={() => handleToggleStatus(cls)}
                                                    title={cls.status === 'active' ? "Désactiver" : "Activer"}
                                                >
                                                    <Power className="h-4 w-4" />
                                                </Button>
                                                {cls.edit_status === 'draft' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-all "
                                                        onClick={() => handleValidate(cls)}
                                                        title="Valider la classe"
                                                    >
                                                        <ShieldCheck className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {cls.edit_status === 'validated' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-all "
                                                        onClick={() => handleResyncEdusign(cls)}
                                                        disabled={resyncingClassId === cls.id}
                                                        title="Resync EduSign"
                                                    >
                                                        <RefreshCw className={`h-4 w-4 ${resyncingClassId === cls.id ? 'animate-spin' : ''}`} />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 transition-all"
                                                    onClick={() => viewDetails(cls)}
                                                    title="Voir les détails"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                {canViewStats && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-all "
                                                        onClick={() => fetchStats(cls.id)}
                                                    >
                                                        <BarChart2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className={`h-8 w-8 transition-all  ${cls.edit_status === 'validated' ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:text-primary hover:bg-primary/5'}`}
                                                    onClick={() => openEditModal(cls)}
                                                    disabled={cls.edit_status === 'validated'}
                                                    title={cls.edit_status === 'validated' ? "Classe verrouillée" : "Modifier"}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all "
                                                    onClick={() => handleDeleteClass(cls.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};

export default VirtualClassTable;
